'use client'

import {
  CartesianGrid,
  Line,
  LineChart as LineChartRecharts,
  XAxis,
  YAxis,
} from 'recharts'

import { cn } from '@/lib/cn'

import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../chart'

type LineChartProps<T> = {
  id?: string
  config: ChartConfig<{ isDashed?: boolean }>
  data: ({ label: string } & Record<string, T>)[]
  tooltip?: boolean
  alternateTick?: 'even' | 'odd'
  className?: string
  legend?: boolean
  yAxisFormatter?: (value: number) => string
  tooltipFormatter?: (value: number) => string
}

const LineChart = <T,>({
  id,
  config,
  data,
  tooltip = true,
  alternateTick,
  className,
  legend = false,
  yAxisFormatter,
  tooltipFormatter,
}: LineChartProps<T>) => {
  const keys = Object.keys(data[0] || {}).filter((key) => key !== 'label')

  return (
    <ChartContainer
      id={id}
      config={config}
      className={cn('h-auto min-h-56', className)}
    >
      <LineChartRecharts
        accessibilityLayer
        data={data}
        margin={{
          top: 12,
          left: 12,
          right: 12,
          bottom: 12,
        }}
      >
        <CartesianGrid vertical={false} />
        <YAxis
          type="number"
          tickLine={false}
          tickMargin={12}
          axisLine={false}
          tickFormatter={yAxisFormatter}
          domain={[0, 'auto']}
        />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval={0}
          textAnchor="middle"
          padding={{ left: 24, right: 24 }}
          tickFormatter={(value, index) => {
            const correctedIndex = index + 1
            if (!alternateTick) return value
            if (index === data.length - 1) return value
            const isEven = correctedIndex % 2 === 0
            return (alternateTick === 'even' && isEven) ||
              (alternateTick === 'odd' && !isEven)
              ? value
              : ''
          }}
        />
        {tooltip && (
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                valueFormatter={(value) => {
                  const number = Number(value.replace(',', ''))
                  return tooltipFormatter ? tooltipFormatter(number) : value
                }}
              />
            }
          />
        )}
        {legend && <ChartLegend content={<ChartLegendContent />} />}
        {keys.map((key) => {
          const itemConfig = config[key]

          return (
            <Line
              key={`line-${key}`}
              dataKey={key}
              type="monotone"
              className={itemConfig.chartClassName}
              stroke={itemConfig.chartClassName}
              strokeWidth={2}
              {...(itemConfig?.isDashed ? { strokeDasharray: '5 5' } : {})}
              dot={{
                className: cn(itemConfig.chartClassName, 'antialiased'),
                strokeWidth: 1,
              }}
              activeDot={{
                r: 6,
                className: cn(itemConfig.chartClassName, 'antialiased'),
                strokeWidth: 1,
              }}
            />
          )
        })}
      </LineChartRecharts>
    </ChartContainer>
  )
}
LineChart.displayName = 'LineChart'

export { LineChart, type LineChartProps }
